import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

// Platform-admin view (full template including prompts)
export type AdminPromptTemplate = {
  id: string;
  label: string;
  description: string | null;
  category: string | null;
  icon: string | null;
  system_prompt: string;
  user_prompt_template: string;
  image_prompt_template: string | null;
  image_style_suffix: string | null;
  default_settings_json: string | null;
  sort_order: number;
  enabled: number;
  is_system: number;
  created_at: number;
  updated_at: number;
};

// User-facing view (prompts hidden)
export type UserPromptTemplate = {
  id: string;
  label: string;
  description: string | null;
  category: string | null;
  icon: string | null;
  default_settings_json: string | null;
  sort_order: number;
  has_image_prompt: number;
};

export type TemplateInput = Omit<
  AdminPromptTemplate,
  'is_system' | 'created_at' | 'updated_at' | 'enabled'
> & { enabled: boolean };

// ---------- Platform (admin) ----------

export function usePlatformPromptTemplates() {
  return useQuery({
    queryKey: ['platform', 'prompt-templates'],
    queryFn: () => api.get<{ templates: AdminPromptTemplate[] }>('/api/platform/prompt-templates'),
  });
}

export function usePlatformPromptTemplate(id: string | null) {
  return useQuery({
    queryKey: ['platform', 'prompt-templates', id],
    queryFn: () =>
      api.get<{ template: AdminPromptTemplate }>(`/api/platform/prompt-templates/${id}`),
    enabled: !!id,
  });
}

export function useCreatePromptTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TemplateInput) =>
      api.post<{ id: string }>('/api/platform/prompt-templates', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'prompt-templates'] }),
  });
}

export function useUpdatePromptTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Partial<Omit<TemplateInput, 'id'>>) =>
      api.put<{ updated: number }>(`/api/platform/prompt-templates/${id}`, patch),
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ['platform', 'prompt-templates', v.id] }).then(() =>
        qc.invalidateQueries({ queryKey: ['platform', 'prompt-templates'] }),
      ),
  });
}

export function useDeletePromptTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.del<{ deleted?: boolean; disabled?: boolean }>(`/api/platform/prompt-templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'prompt-templates'] }),
  });
}

export type PreviewResult = {
  system_prompt: string;
  user_prompt: string;
  image_prompt: string | null;
};

export function usePreviewPromptTemplate() {
  return useMutation({
    mutationFn: ({
      id,
      vars,
    }: {
      id: string;
      vars: {
        focus_keyword?: string;
        title?: string;
        language?: string;
        tone?: string;
        audience?: string;
        target_words?: number;
        include_faq?: boolean;
        custom_instructions?: string;
      };
    }) => api.post<PreviewResult>(`/api/platform/prompt-templates/${id}/preview`, vars),
  });
}

// ---------- User-facing (labels only) ----------

export function useContentPromptTemplates() {
  return useQuery({
    queryKey: ['content', 'prompt-templates'],
    queryFn: () =>
      api.get<{ templates: UserPromptTemplate[] }>('/api/app/content/prompt-templates'),
    staleTime: 5 * 60 * 1000,
  });
}
